/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import { ApolloError, useQuery, useMutation } from '@apollo/client';
import { sections } from '../../pages/SavedListingsPage/SavedListingsPage.types';
import {
    SAVED_PROPERTIES_QUERY,
    DELETE_SAVED_PROPERTIES_MUTATION
} from 'listing-details/queries/SavedListings.query';
import { SavedListingSection } from 'listing-details/types';
import { trackDeleteSavedListing } from '../../pages/SavedListingsPage/SavedListingsPage.tracking';
import groupBy from 'lodash/groupBy';
import { getListingType } from '../../pages/SavedListingsPage/SavedListingsPage.helpers';
import { Saved_ListingFragment } from 'listing-details/queries/SavedListing.fragment.generated';
import { GetSavedPropertiesQuery } from 'listing-details/queries/SavedListings.query.generated';

// object containing all section keys and user's savedListings for each section
export type SavedListingsBySection = Record<
    SavedListingSection,
    Array<Saved_ListingFragment>
>;

// an array containing the sections the user has saved listings for
// (in correct index order), also containing the listings for that section.
export type OrderedFilteredSavedListingsBySection = Array<{
    listings: Saved_ListingFragment[];
    key: SavedListingSection;
    label: string;
}>;

const getSavedListingFragmentsFromSavedListingData = (
    data: GetSavedPropertiesQuery | undefined
): Array<Saved_ListingFragment> => {
    if (!data || !('listings' in data.savedProperties)) return [];
    return data.savedProperties.listings?.map(({ listing }) => listing) || [];
};

const getListingsBySection = (
    savedListings: Array<Saved_ListingFragment>
): SavedListingsBySection => {
    if (!savedListings.length) {
        return sections.reduce(
            (map, section) => ({ ...map, [section.key]: [] }),
            {}
        ) as SavedListingsBySection;
    }

    return groupBy(savedListings, getListingType) as SavedListingsBySection;
};

const getOrderedFilteredSavedListingsBySection = (
    savedListings: Array<Saved_ListingFragment>,
    savedListingsBySection: SavedListingsBySection
): OrderedFilteredSavedListingsBySection => {
    if (!savedListings.length) return [];
    return sections
        .map((section) => ({
            ...section,
            listings: savedListingsBySection[section.key]
        }))
        .filter((section) => !!section.listings?.length);
};

export const useSavedListings = (
    skip = false
): {
    savedListings: Array<Saved_ListingFragment>;
    savedListingsBySection: SavedListingsBySection;
    orderedFilteredSavedListingsBySection: OrderedFilteredSavedListingsBySection;
    loading: boolean;
    hasError: boolean;
} => {
    const [hasError, setHasError] = useState<boolean>(false);
    const { data, loading, error } = useQuery<GetSavedPropertiesQuery>(
        SAVED_PROPERTIES_QUERY,
        {
            ssr: false,
            skip
        }
    );

    useEffect(() => {
        if ((data && 'errorCode' in data.savedProperties) || error) {
            setHasError(true);
        }
    }, [error, data?.savedProperties]);

    const savedListings = getSavedListingFragmentsFromSavedListingData(data);
    const savedListingsBySection = getListingsBySection(savedListings);
    const orderedFilteredSavedListingsBySection = getOrderedFilteredSavedListingsBySection(
        savedListings,
        savedListingsBySection
    );

    return {
        savedListings,
        savedListingsBySection,
        orderedFilteredSavedListingsBySection,
        loading,
        hasError
    };
};

export const useDeleteSavedListing = (): {
    deleteSavedListing: (listingIdToDelete: string) => void;
    data?: { deleteFavouriteListing: boolean };
    error?: ApolloError | null;
    closeError: () => void;
} => {
    const [deleteError, setDeleteError] = useState<ApolloError | null>(null);

    const closeError = () => setDeleteError(null);

    const [deleteSavedListingMutation, { data }] = useMutation(
        DELETE_SAVED_PROPERTIES_MUTATION,
        {
            onError(error) {
                setDeleteError(error);
            }
        }
    );

    const deleteSavedListing = (listingIdToDelete: string) => {
        deleteSavedListingMutation({
            variables: { listingIds: [Number(listingIdToDelete)] },
            optimisticResponse: {
                deleteUserListings: { successCode: 'deleted' }
            },
            update(cache, { data }) {
                if (data?.deleteUserListings?.successCode !== 'deleted') return;
                cache.modify({
                    fields: {
                        savedProperties(existingSavedProperties = {}) {
                            const newListings = existingSavedProperties?.listings.filter(
                                (listing: {
                                    listing: Saved_ListingFragment;
                                }) => {
                                    return (
                                        listing.listing.listingId !==
                                        listingIdToDelete
                                    );
                                }
                            );
                            const newListingIds = existingSavedProperties?.listingIds.filter(
                                (listingId: string) => {
                                    return listingId !== listingIdToDelete;
                                }
                            );
                            const newSavedProperties = {
                                ...existingSavedProperties,
                                listings: newListings,
                                listingIds: newListingIds
                            };
                            return newSavedProperties;
                        }
                    }
                });
            }
        });
        trackDeleteSavedListing();
    };

    return {
        deleteSavedListing,
        data,
        error: deleteError,
        closeError
    };
};
